import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Users, Car, ClipboardCheck } from "lucide-react";
import { API } from "../auth/AuthContext";
import AdminVehicleReview from "../components/AdminVehicleReview";
import AdminParkingReview from "../components/AdminParkingReview";
import AdminStat from "../components/admin/AdminStat";
import UserActivityModal from "../components/admin/UserActivityModal";
import UserEditModal from "../components/admin/UserEditModal";
import UserDeleteModal from "../components/admin/UserDeleteModal";
import AdminListingDeleteModal from "../components/admin/AdminListingDeleteModal";
import { ADMIN_PAGE_SIZE } from "../components/admin/adminConstants";
import { buildAdminQuery } from "../components/admin/adminQuery";
import { pageItems } from "../lib/pageItems";
import { toast } from "sonner";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import AdminFraudTab from "./admin/AdminFraudTab";
import AdminSupportTab from "./admin/AdminSupportTab";
import AdminParkingTab from "./admin/AdminParkingTab";
import AdminAccreditationTab from "./admin/AdminAccreditationTab";
import AdminKycTab from "./admin/AdminKycTab";
import AdminFractionalTab from "./admin/AdminFractionalTab";
import AdminPendingVehiclesTab from "./admin/AdminPendingVehiclesTab";
import AdminAllListingsTab from "./admin/AdminAllListingsTab";
import AdminEsimTab from "./admin/AdminEsimTab";
import AdminPartnerTab from "./admin/AdminPartnerTab";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminBookingsTab from "./admin/AdminBookingsTab";
import AdminPartnerBookingDetails from "../components/admin/AdminPartnerBookingDetails";
import CancellationReviewDialog from "../components/CancellationReviewDialog";
import { vehicleRejectionBody } from "../lib/vehicleRejection";

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [pendingParking, setPendingParking] = useState([]);
  const [pendingOfferings, setPendingOfferings] = useState([]);
  const [pendingKyc, setPendingKyc] = useState([]);
  const [pendingAccred, setPendingAccred] = useState([]);
  const [fraudQueue, setFraudQueue] = useState([]);
  const [, setFraudNextCursor] = useState(null);
  const [fraudTotal, setFraudTotal] = useState(null);
  const [fraudPage, setFraudPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [esimOverview, setEsimOverview] = useState(null);
  const [esimActivations, setEsimActivations] = useState([]);
  const [esimOrderLogs, setEsimOrderLogs] = useState([]);
  const [esimLogsFilter, setEsimLogsFilter] = useState("");
  const [esimLoading, setEsimLoading] = useState(false);
  const [esimCatalogSyncing, setEsimCatalogSyncing] = useState(false);
  const [tab, setTab] = useState(() => searchParams.get("tab") || "pending");
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteListing, setDeleteListing] = useState(null);
  const [reviewVehicleId, setReviewVehicleId] = useState(null);
  const [reviewParkingId, setReviewParkingId] = useState(null);
  const [activityUser, setActivityUser] = useState(null);
  const [bookingsStatus, setBookingsStatus] = useState("all");
  const [bookingsSearch, setBookingsSearch] = useState("");
  const [bookingsSearchDraft, setBookingsSearchDraft] = useState("");
  const [, setBookingsNextCursor] = useState(null);
  const [bookingsTotal, setBookingsTotal] = useState(null);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersSearchDraft, setUsersSearchDraft] = useState("");
  const [, setUsersNextCursor] = useState(null);
  const [usersTotal, setUsersTotal] = useState(null);
  const [usersPage, setUsersPage] = useState(1);
  const [pendingSearch, setPendingSearch] = useState("");
  const [pendingSearchDraft, setPendingSearchDraft] = useState("");
  const [, setPendingNextCursor] = useState(null);
  const [pendingTotal, setPendingTotal] = useState(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [allListingsSearch, setAllListingsSearch] = useState("");
  const [allListingsSearchDraft, setAllListingsSearchDraft] = useState("");
  const [, setAllListingsNextCursor] = useState(null);
  const [allListingsTotal, setAllListingsTotal] = useState(null);
  const [allListingsPage, setAllListingsPage] = useState(1);
  const [parkingSearch, setParkingSearch] = useState("");
  const [parkingSearchDraft, setParkingSearchDraft] = useState("");
  const [, setParkingNextCursor] = useState(null);
  const [parkingTotal, setParkingTotal] = useState(null);
  const [parkingPage, setParkingPage] = useState(1);
  const [kycSearch, setKycSearch] = useState("");
  const [kycSearchDraft, setKycSearchDraft] = useState("");
  const [, setKycNextCursor] = useState(null);
  const [kycTotal, setKycTotal] = useState(null);
  const [kycPage, setKycPage] = useState(1);
  const [partnerBookings, setPartnerBookings] = useState([]);
  const [partnerTotal, setPartnerTotal] = useState(null);
  const [partnerPage, setPartnerPage] = useState(1);
  const [partnerBookingDetail, setPartnerBookingDetail] = useState(null);
  const [partnerCancelBooking, setPartnerCancelBooking] = useState(null);
  const [supportCases, setSupportCases] = useState([]);
  const [supportStatus, setSupportStatus] = useState("open");
  const [, setSupportNextCursor] = useState(null);
  const [supportTotal, setSupportTotal] = useState(null);
  const [supportPage, setSupportPage] = useState(1);
  const [supportLoading, setSupportLoading] = useState(false);
  const [selectedSupportCase, setSelectedSupportCase] = useState(null);
  const [supportPatchStatus, setSupportPatchStatus] = useState("open");
  const [supportPatchNotes, setSupportPatchNotes] = useState("");
  const [supportPatching, setSupportPatching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);

  const applyPage = (setItems, setNext, setTotal) => (data, append = false) => {
    const items = pageItems(data);
    setItems((prev) => (append ? [...prev, ...items] : items));
    setNext(data?.next_cursor ?? null);
    setTotal(data?.total ?? null);
  };

  const fetchAdminPage = async (path, { q, cursor, extra = {}, limit = ADMIN_PAGE_SIZE } = {}) => {
    const qs = buildAdminQuery({ q, cursor, limit, extra });
    const r = await axios.get(`${API}${path}${qs}`, { withCredentials: true });
    return r.data;
  };

  const loadBookings = async ({ page = 1, q = bookingsSearch, status = bookingsStatus } = {}) => {
    try {
      const extra = {};
      if (status === "active") extra.active = "true";
      else if (status !== "all") extra.status = status;
      extra.page = page;
      const data = await fetchAdminPage("/admin/bookings", { q, extra });
      applyPage(setAllBookings, setBookingsNextCursor, setBookingsTotal)(data, false);
      setBookingsPage(page);
    } catch (e) {
      toast.error("Could not load bookings");
    }
  };

  const loadUsers = async ({ page = 1, q = usersSearch } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/users", { q, extra: { page } });
      applyPage(setUsers, setUsersNextCursor, setUsersTotal)(data, false);
      setUsersPage(page);
    } catch (e) {
      toast.error("Could not load users");
    }
  };

  const loadPendingVehicles = async ({ page = 1, q = pendingSearch } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/vehicles", { q, extra: { status: "pending", page } });
      applyPage(setPending, setPendingNextCursor, setPendingTotal)(data, false);
      setPendingPage(page);
    } catch (e) {
      toast.error("Could not load pending vehicles");
    }
  };

  const loadAllListings = async ({ page = 1, q = allListingsSearch } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/listings", { q, extra: { page } });
      applyPage(setAllListings, setAllListingsNextCursor, setAllListingsTotal)(data, false);
      setAllListingsPage(page);
    } catch (e) {
      toast.error("Could not load listings");
    }
  };

  const loadPendingParking = async ({ page = 1, q = parkingSearch } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/parking", { q, extra: { status: "pending", page } });
      applyPage(setPendingParking, setParkingNextCursor, setParkingTotal)(data, false);
      setParkingPage(page);
    } catch (e) {
      toast.error("Could not load pending parking");
    }
  };

  const loadPendingKyc = async ({ page = 1, q = kycSearch } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/kyc/pending", { q, extra: { page } });
      applyPage(setPendingKyc, setKycNextCursor, setKycTotal)(data, false);
      setKycPage(page);
    } catch (e) {
      toast.error("Could not load KYC queue");
    }
  };

  const loadFraudQueue = async ({ page = 1 } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/inspections/fraud-queue", { extra: { page } });
      applyPage(setFraudQueue, setFraudNextCursor, setFraudTotal)(data, false);
      setFraudPage(page);
    } catch (e) {
      toast.error("Could not load fraud queue");
    }
  };

  const loadPartnerBookings = async ({ page = 1 } = {}) => {
    try {
      const data = await fetchAdminPage("/admin/bookings", {
        extra: { external_partner: "true", page },
      });
      applyPage(setPartnerBookings, () => {}, setPartnerTotal)(data, false);
      setPartnerPage(page);
    } catch (e) {
      toast.error("Could not load partner bookings");
    }
  };

  const load = async () => {
    setLoading(true);
    setAllListingsPage(1);
    setBookingsPage(1);
    setUsersPage(1);
    setPendingPage(1);
    setParkingPage(1);
    setKycPage(1);
    setSupportPage(1);
    const pickPage = (r) => pageItems(r.data);
    const pickRaw = (r) => r.data;
    const sections = [
      { label: "stats", set: setStats, pick: pickRaw },
      { label: "pending vehicles", set: setPending, pick: pickPage, meta: [setPendingNextCursor, setPendingTotal] },
      { label: "all vehicles", set: setAllVehicles, pick: pickPage },
      { label: "listings", set: setAllListings, pick: pickPage, meta: [setAllListingsNextCursor, setAllListingsTotal] },
      { label: "pending parking", set: setPendingParking, pick: pickPage, meta: [setParkingNextCursor, setParkingTotal] },
      { label: "fractional", set: setPendingOfferings, pick: (r) => pageItems(r.data), fallback: [] },
      { label: "KYC", set: setPendingKyc, pick: pickPage, meta: [setKycNextCursor, setKycTotal] },
      { label: "accreditation", set: setPendingAccred, pick: pickRaw, fallback: [] },
      { label: "fraud queue", set: setFraudQueue, pick: (r) => pageItems(r.data), fallback: [], meta: [setFraudNextCursor, setFraudTotal] },
      { label: "users", set: setUsers, pick: pickPage, meta: [setUsersNextCursor, setUsersTotal] },
      { label: "bookings", set: setAllBookings, pick: pickPage, meta: [setBookingsNextCursor, setBookingsTotal] },
    ];
    const bookingsExtra = bookingsStatus === "active"
      ? { active: "true" }
      : bookingsStatus !== "all"
        ? { status: bookingsStatus }
        : {};
    const results = await Promise.allSettled([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/vehicles${buildAdminQuery({ q: pendingSearch, extra: { status: "pending", page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/vehicles${buildAdminQuery({ limit: ADMIN_PAGE_SIZE })}`, { withCredentials: true }),
      axios.get(`${API}/admin/listings${buildAdminQuery({ q: allListingsSearch, extra: { page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/parking${buildAdminQuery({ q: parkingSearch, extra: { status: "pending", page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/fractional/pending`, { withCredentials: true }),
      axios.get(`${API}/admin/kyc/pending${buildAdminQuery({ q: kycSearch, extra: { page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/investor/accreditations/pending`, { withCredentials: true }),
      axios.get(`${API}/admin/inspections/fraud-queue${buildAdminQuery({ extra: { page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/users${buildAdminQuery({ q: usersSearch, extra: { page: 1 } })}`, { withCredentials: true }),
      axios.get(`${API}/admin/bookings${buildAdminQuery({ q: bookingsSearch, extra: { ...bookingsExtra, page: 1 } })}`, { withCredentials: true }),
    ]);
    const failed = [];
    results.forEach((r, i) => {
      const sec = sections[i];
      if (r.status === "fulfilled") {
        sec.set(sec.pick(r.value));
        if (sec.meta && r.value.data && !Array.isArray(r.value.data)) {
          sec.meta[0](r.value.data.next_cursor ?? null);
          sec.meta[1](r.value.data.total ?? null);
        }
      } else {
        if ("fallback" in sec) sec.set(sec.fallback);
        failed.push(sec.label);
      }
    });
    setLoadErrors(failed);
    if (failed.length === results.length) toast.error("Admin only");
    setLoading(false);
  };

  const loadSupportCases = async ({ page = 1, status = supportStatus } = {}) => {
    setSupportLoading(true);
    try {
      const extra = status !== "all" ? { status } : {};
      extra.page = page;
      const data = await fetchAdminPage("/admin/support/cases", { extra });
      applyPage(setSupportCases, setSupportNextCursor, setSupportTotal)(data, false);
      setSupportPage(page);
    } catch (e) {
      toast.error("Could not load support cases");
    } finally {
      setSupportLoading(false);
    }
  };

  const patchSupportCase = async () => {
    if (!selectedSupportCase) return;
    setSupportPatching(true);
    try {
      const r = await axios.patch(
        `${API}/admin/support/cases/${selectedSupportCase.case_id}`,
        { status: supportPatchStatus, admin_notes: supportPatchNotes || null },
        { withCredentials: true },
      );
      toast.success("Case updated");
      setSupportCases((prev) => prev.map((c) => (c.case_id === r.data.case_id ? r.data : c)));
      setSelectedSupportCase(r.data);
      loadSupportCases({ status: supportStatus });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSupportPatching(false);
    }
  };

  const openSupportCase = (c) => {
    setSelectedSupportCase(c);
    setSupportPatchStatus(c.status || "open");
    setSupportPatchNotes(c.admin_notes || "");
  };

  const loadEsim = async () => {
    setEsimLoading(true);
    try {
      const q = esimLogsFilter ? `?outcome=${encodeURIComponent(esimLogsFilter)}&limit=50` : "?limit=50";
      const [overview, activations, logs] = await Promise.all([
        axios.get(`${API}/admin/esim/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/esim`, { withCredentials: true }),
        axios.get(`${API}/admin/esim/order-logs${q}`, { withCredentials: true }),
      ]);
      setEsimOverview(overview.data);
      setEsimActivations(pageItems(activations.data));
      setEsimOrderLogs(pageItems(logs.data));
    } catch (e) {
      toast.error("Could not load eSIM admin data");
    } finally {
      setEsimLoading(false);
    }
  };

  const syncEsimCatalog = async () => {
    setEsimCatalogSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/esim/catalog/sync`, {}, { withCredentials: true });
      toast.success(`Catalog synced — ${r.data?.package_count ?? "?"} packages`);
      await loadEsim();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Catalog sync failed");
    } finally {
      setEsimCatalogSyncing(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "esim") loadEsim();
    if (tab === "partner") loadPartnerBookings();
    if (tab === "support") loadSupportCases();
    /* eslint-disable-next-line */
  }, [tab, esimLogsFilter]);

  const toastRejectionEmail = (data, label) => {
    const status = data?.email_status;
    if (status === "sent") {
      toast.success(`${label} — rejection email sent`);
    } else if (status === "failed") {
      toast.error(`${label} — email failed${data.email_error ? `: ${data.email_error}` : ""}`);
    } else if (status === "skipped") {
      toast.warning(`${label} — email skipped${data.email_error ? `: ${data.email_error}` : ""}`);
    } else {
      toast.success(label);
    }
  };

  const approve = async (id) => {
    await axios.post(`${API}/admin/vehicles/${id}/approve`, {}, { withCredentials: true });
    toast.success("Approved");
    await Promise.all([load(), loadFraudQueue()]);
  };
  const reject = async (id, reason, freshAiImagesRequired = true) => {
    const body = vehicleRejectionBody(reason, freshAiImagesRequired);
    const { data } = await axios.post(`${API}/admin/vehicles/${id}/reject`, body, { withCredentials: true });
    toastRejectionEmail(data, "Rejected");
    load();
  };
  const toDeleteListing = (item) => ({
    type: item.type || (item.spot_id ? "parking" : "vehicle"),
    listing_id: item.listing_id || item.vehicle_id || item.spot_id,
    title: item.title
      || `${item.year || ""} ${item.make || ""} ${item.model || ""}`.trim()
      || item.name
      || "Listing",
    _raw: item,
  });

  const openDeleteListing = (item) => setDeleteListing(toDeleteListing(item));

  const patchListingStatus = async (item, status) => {
    const id = item.listing_id || item.vehicle_id || item.spot_id;
    const isParking = item.type === "parking" || item.spot_id;
    const path = isParking ? `/parking/${id}/status` : `/vehicles/${id}/status`;
    try {
      await axios.patch(`${API}${path}`, { status }, { withCredentials: true });
      toast.success(status === "paused" ? "Listing paused" : "Listing resumed");
      setReviewVehicleId(null);
      setReviewParkingId(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "active_bookings_exist") {
        toast.error("Cannot change status while active bookings exist");
      } else {
        toast.error(detail || "Status update failed");
      }
      throw e;
    }
  };

  const pauseListing = (item) => patchListingStatus(item, "paused");
  const resumeListing = (item) => patchListingStatus(item, "approved");

  const deleteVehicleListing = async (vehicle, { force = false } = {}) => {
    try {
      await axios.delete(`${API}/admin/vehicles/${vehicle.vehicle_id}${force ? "?force=true" : ""}`, { withCredentials: true });
      toast.success("Listing deleted");
      setDeleteListing(null);
      setReviewVehicleId(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "active_bookings_exist" && !force) {
        throw new Error("active_bookings_exist");
      }
      toast.error(detail || "Delete failed");
      throw e;
    }
  };

  const deleteParkingListing = async (spot, { force = false } = {}) => {
    const spotId = spot.spot_id || spot.listing_id;
    try {
      await axios.delete(`${API}/admin/parking/${spotId}${force ? "?force=true" : ""}`, { withCredentials: true });
      toast.success("Parking listing deleted");
      setDeleteListing(null);
      setReviewParkingId(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "active_bookings_exist" && !force) {
        throw new Error("active_bookings_exist");
      }
      toast.error(detail || "Delete failed");
      throw e;
    }
  };

  const deleteListingByType = async (listing, opts) => {
    if (listing.type === "parking") {
      await deleteParkingListing(listing._raw || listing, opts);
    } else {
      const raw = listing._raw || listing;
      await deleteVehicleListing(
        raw.vehicle_id ? raw : { vehicle_id: listing.listing_id, ...listing },
        opts,
      );
    }
  };
  const approveParking = async (id) => { await axios.post(`${API}/admin/parking/${id}/approve`, {}, { withCredentials: true }); toast.success("Parking approved"); load(); };
  const rejectParking = async (id, reason) => {
    const body = reason ? { reason } : {};
    const { data } = await axios.post(`${API}/admin/parking/${id}/reject`, body, { withCredentials: true });
    toastRejectionEmail(data, "Parking rejected");
    load();
  };
  const approveOffering = async (id) => { await axios.post(`${API}/admin/fractional/${id}/approve`, {}, { withCredentials: true }); toast.success("Offering approved"); load(); };
  const rejectOffering = async (id) => { await axios.post(`${API}/admin/fractional/${id}/reject`, {}, { withCredentials: true }); toast.success("Offering rejected"); load(); };
  const approveKyc = async (uid) => { await axios.post(`${API}/admin/kyc/${uid}/approve`, {}, { withCredentials: true }); toast.success("KYC approved"); load(); };
  const rejectKyc = async (uid) => { await axios.post(`${API}/admin/kyc/${uid}/reject`, {}, { withCredentials: true }); toast.success("KYC rejected"); load(); };
  const queueKycReview = async (uid) => {
    await axios.patch(`${API}/admin/users/${uid}`, { kyc_status: "manual_review" }, { withCredentials: true });
    toast.success("User queued for KYC review");
    load();
  };
  const approveAccred = async (uid) => { await axios.post(`${API}/admin/investor/${uid}/accreditation/approve`, {}, { withCredentials: true }); toast.success("Accreditation approved"); load(); };
  const rejectAccred = async (uid) => { await axios.post(`${API}/admin/investor/${uid}/accreditation/reject`, {}, { withCredentials: true }); toast.success("Accreditation rejected"); load(); };
  const partnerConfirm = async (id) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/partner-confirm`, {}, { withCredentials: true });
      toast.success("Partner booking confirmed");
      load();
      loadPartnerBookings({ page: partnerPage });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
  };
  const partnerReject = async (id) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/partner-reject`, {}, { withCredentials: true });
      toast.success("Partner booking cancelled");
      loadPartnerBookings({ page: partnerPage });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const partnerMarkFulfilled = async (id) => {
    try {
      await axios.post(`${API}/admin/bookings/${id}/partner-fulfill`, {}, { withCredentials: true });
      toast.success("Marked as fulfilled");
      loadPartnerBookings({ page: partnerPage });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark fulfilled"); }
  };

  const pendingPartnerCount = partnerBookings.filter(
    (b) => b.status === "pending_partner_confirmation",
  ).length;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="eyebrow mb-2">Operations</div>
      <h1 className="zc-headline text-4xl md:text-5xl">Admin control room</h1>

      {loading && (
        <p className="text-sm text-[var(--zc-text-3)] mt-4">Loading dashboard…</p>
      )}
      {loadErrors.length > 0 && (
        <div className="mt-4 p-3 rounded-lg border border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.08)] text-sm flex flex-wrap items-center justify-between gap-2">
          <span className="text-[var(--zc-text-2)]">
            Some sections failed to load: {loadErrors.join(", ")}.
          </span>
          <button type="button" onClick={load} className="px-3 h-8 zc-btn-ghost text-xs font-semibold">
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
        <AdminStat label="Users" v={stats?.users} icon={<Users size={14} />} />
        <AdminStat label="Vehicles" v={stats?.vehicles} icon={<Car size={14} />} />
        <AdminStat label="Pending approvals" v={stats?.pending_total ?? stats?.vehicles_pending} icon={<ClipboardCheck size={14} />} accent />
        {(stats?.parking_pending > 0 || stats?.vehicles_pending > 0) && (
          <div className="col-span-2 md:col-span-5 text-xs text-[var(--zc-text-3)] -mt-2">
            {stats?.vehicles_pending || 0} vehicle{stats?.vehicles_pending === 1 ? "" : "s"} · {stats?.parking_pending || 0} parking
          </div>
        )}
        <AdminStat
          label="Bookings"
          v={stats?.bookings}
          icon={<ClipboardCheck size={14} />}
          onClick={() => { setTab("bookings"); setBookingsStatus("all"); loadBookings({ page: 1, status: "all" }); }}
          testid="admin-stat-bookings"
        />
        <AdminStat
          label="Active"
          v={stats?.bookings_active}
          icon={<ClipboardCheck size={14} />}
          onClick={() => { setTab("bookings"); setBookingsStatus("active"); loadBookings({ page: 1, status: "active" }); }}
          testid="admin-stat-active-rentals"
        />
      </div>

      <div className="flex overflow-x-auto no-scrollbar flex-nowrap gap-1 mt-10 border-b border-[var(--zc-border)] -mx-4 px-4 sm:mx-0 sm:px-0">
        {["pending","fraud","support","bookings","vehicles","parking",...(FRACTIONAL_ENABLED ? ["fractional"] : []),"kyc","accreditation","partner","esim","users"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={"px-4 h-10 text-xs uppercase tracking-widest font-semibold whitespace-nowrap " + (tab === t ? "text-[var(--zc-text)] border-b-2 border-[var(--zc-accent)]" : "text-[var(--zc-text-3)]")}
            data-testid={`admin-tab-${t}`}>
            {t === "pending" ? `Pending vehicles${pending.length ? ` (${pending.length})` : ""}`
              : t === "fraud" ? `Inspection fraud${fraudQueue.length ? ` (${fraudQueue.length})` : ""}`
              : t === "support" ? `Support${supportCases.length ? ` (${supportCases.length})` : ""}`
              : t === "vehicles" ? `All listings${allListings.length ? ` (${allListings.length})` : ""}`
              : t === "parking" ? `Pending parking${pendingParking.length ? ` (${pendingParking.length})` : ""}`
              : t === "fractional" ? `Fractional${pendingOfferings.length ? ` (${pendingOfferings.length})` : ""}`
              : t === "kyc" ? `KYC review${pendingKyc.length ? ` (${pendingKyc.length})` : ""}`
              : t === "accreditation" ? `Accreditation${pendingAccred.length ? ` (${pendingAccred.length})` : ""}`
              : t === "partner" ? `Partner${partnerTotal ? ` (${partnerTotal})` : pendingPartnerCount ? ` (${pendingPartnerCount} pending)` : ""}`
              : t === "esim" ? `eSIM${esimOverview?.counts?.order_logs_failed ? ` (${esimOverview.counts.order_logs_failed} fail)` : ""}`
              : t === "users" ? "Users"
              : t === "bookings" ? "Bookings"
              : t}
          </button>
        ))}
      </div>

      {tab === "fraud" && (
        <AdminFraudTab
          fraudQueue={fraudQueue}
          page={fraudPage}
          totalPages={Math.max(1, Math.ceil((fraudTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadFraudQueue({ page: p })}
        />
      )}

      {tab === "support" && (
        <AdminSupportTab
          supportCases={supportCases}
          supportStatus={supportStatus}
          supportLoading={supportLoading}
          selectedSupportCase={selectedSupportCase}
          supportPatchStatus={supportPatchStatus}
          supportPatchNotes={supportPatchNotes}
          supportPatching={supportPatching}
          onStatusFilter={(id) => { setSupportStatus(id); loadSupportCases({ page: 1, status: id }); }}
          onOpenCase={openSupportCase}
          onPatchStatusChange={setSupportPatchStatus}
          onPatchNotesChange={setSupportPatchNotes}
          onPatchSave={patchSupportCase}
          page={supportPage}
          totalPages={Math.max(1, Math.ceil((supportTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadSupportCases({ page: p, status: supportStatus })}
        />
      )}

      {tab === "parking" && (
        <AdminParkingTab
          pendingParking={pendingParking}
          parkingSearchDraft={parkingSearchDraft}
          onParkingSearchDraftChange={setParkingSearchDraft}
          onParkingSearch={() => { setParkingSearch(parkingSearchDraft); loadPendingParking({ page: 1, q: parkingSearchDraft }); }}
          parkingTotal={parkingTotal}
          page={parkingPage}
          totalPages={Math.max(1, Math.ceil((parkingTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadPendingParking({ page: p, q: parkingSearch })}
          onReview={setReviewParkingId}
          onApprove={approveParking}
          onDelete={openDeleteListing}
        />
      )}

      {tab === "accreditation" && (
        <AdminAccreditationTab
          pendingAccred={pendingAccred}
          onApprove={approveAccred}
          onReject={rejectAccred}
        />
      )}

      {tab === "kyc" && (
        <AdminKycTab
          pendingKyc={pendingKyc}
          kycSearchDraft={kycSearchDraft}
          onKycSearchDraftChange={setKycSearchDraft}
          onKycSearch={() => { setKycSearch(kycSearchDraft); loadPendingKyc({ page: 1, q: kycSearchDraft }); }}
          kycTotal={kycTotal}
          page={kycPage}
          totalPages={Math.max(1, Math.ceil((kycTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadPendingKyc({ page: p, q: kycSearch })}
          onApprove={approveKyc}
          onReject={rejectKyc}
        />
      )}

      {tab === "fractional" && (
        <AdminFractionalTab
          pendingOfferings={pendingOfferings}
          onApprove={approveOffering}
          onReject={rejectOffering}
        />
      )}

      {tab === "pending" && (
        <AdminPendingVehiclesTab
          pending={pending}
          pendingSearchDraft={pendingSearchDraft}
          onPendingSearchDraftChange={setPendingSearchDraft}
          onPendingSearch={() => { setPendingSearch(pendingSearchDraft); loadPendingVehicles({ page: 1, q: pendingSearchDraft }); }}
          pendingTotal={pendingTotal}
          page={pendingPage}
          totalPages={Math.max(1, Math.ceil((pendingTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadPendingVehicles({ page: p, q: pendingSearch })}
          onReview={setReviewVehicleId}
          onApprove={approve}
          onReject={reject}
          onDelete={(v) => openDeleteListing({ ...v, type: "vehicle" })}
        />
      )}

      {tab === "vehicles" && (
        <AdminAllListingsTab
          allListings={allListings}
          allListingsSearchDraft={allListingsSearchDraft}
          onAllListingsSearchDraftChange={setAllListingsSearchDraft}
          onAllListingsSearch={() => { setAllListingsSearch(allListingsSearchDraft); loadAllListings({ page: 1, q: allListingsSearchDraft }); }}
          allListingsTotal={allListingsTotal}
          allListingsPage={allListingsPage}
          allListingsTotalPages={Math.max(1, Math.ceil((allListingsTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadAllListings({ page: p, q: allListingsSearch })}
          onPause={pauseListing}
          onResume={resumeListing}
          onDelete={openDeleteListing}
        />
      )}

      {tab === "esim" && (
        <AdminEsimTab
          esimOverview={esimOverview}
          esimActivations={esimActivations}
          esimOrderLogs={esimOrderLogs}
          esimLoading={esimLoading}
          esimLogsFilter={esimLogsFilter}
          esimCatalogSyncing={esimCatalogSyncing}
          onLogsFilterChange={setEsimLogsFilter}
          onRefresh={loadEsim}
          onSyncCatalog={syncEsimCatalog}
        />
      )}

      {tab === "partner" && (
        <AdminPartnerTab
          partnerBookings={partnerBookings}
          page={partnerPage}
          totalPages={Math.max(1, Math.ceil((partnerTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadPartnerBookings({ page: p })}
          onConfirm={partnerConfirm}
          onReject={partnerReject}
          onCancelRefund={setPartnerCancelBooking}
          onViewDetails={setPartnerBookingDetail}
          onMarkFulfilled={partnerMarkFulfilled}
        />
      )}

      {tab === "users" && (
        <AdminUsersTab
          users={users}
          usersSearchDraft={usersSearchDraft}
          onUsersSearchDraftChange={setUsersSearchDraft}
          onUsersSearch={() => { setUsersSearch(usersSearchDraft); loadUsers({ page: 1, q: usersSearchDraft }); }}
          usersTotal={usersTotal}
          page={usersPage}
          totalPages={Math.max(1, Math.ceil((usersTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadUsers({ page: p, q: usersSearch })}
          onQueueKycReview={queueKycReview}
          onApproveKyc={approveKyc}
          onRejectKyc={rejectKyc}
          onActivity={setActivityUser}
          onEdit={setEditUser}
          onDelete={setDeleteUser}
        />
      )}

      {tab === "bookings" && (
        <AdminBookingsTab
          allBookings={allBookings}
          bookingsStatus={bookingsStatus}
          bookingsSearchDraft={bookingsSearchDraft}
          onBookingsSearchDraftChange={setBookingsSearchDraft}
          onBookingsSearch={() => { setBookingsSearch(bookingsSearchDraft); loadBookings({ page: 1, q: bookingsSearchDraft }); }}
          bookingsTotal={bookingsTotal}
          onStatusFilter={(id) => { setBookingsStatus(id); loadBookings({ page: 1, status: id }); }}
          page={bookingsPage}
          totalPages={Math.max(1, Math.ceil((bookingsTotal || 0) / ADMIN_PAGE_SIZE))}
          onPageChange={(p) => loadBookings({ page: p, q: bookingsSearch, status: bookingsStatus })}
        />
      )}

      <UserEditModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />
      <UserActivityModal user={activityUser} onClose={() => setActivityUser(null)} />
      <UserDeleteModal user={deleteUser} onClose={() => setDeleteUser(null)} onDeleted={() => { setDeleteUser(null); load(); }} />
      <AdminListingDeleteModal
        listing={deleteListing}
        onClose={() => setDeleteListing(null)}
        onDelete={deleteListingByType}
      />
      <AdminVehicleReview
        vehicleId={reviewVehicleId}
        open={!!reviewVehicleId}
        onClose={() => setReviewVehicleId(null)}
        onApprove={approve}
        onReject={reject}
        onDelete={(vehicle) => openDeleteListing({ ...vehicle, type: "vehicle" })}
        onPause={pauseListing}
        onResume={resumeListing}
      />
      <AdminParkingReview
        spotId={reviewParkingId}
        open={!!reviewParkingId}
        onClose={() => setReviewParkingId(null)}
        onApprove={approveParking}
        onReject={rejectParking}
        onDelete={(spot) => openDeleteListing({ ...spot, type: "parking" })}
        onPause={pauseListing}
        onResume={resumeListing}
      />
      <AdminPartnerBookingDetails
        booking={partnerBookingDetail}
        open={!!partnerBookingDetail}
        onClose={() => setPartnerBookingDetail(null)}
      />
      <CancellationReviewDialog
        booking={partnerCancelBooking}
        onClose={() => setPartnerCancelBooking(null)}
        onCancelled={() => {
          setPartnerCancelBooking(null);
          loadPartnerBookings({ page: partnerPage });
        }}
      />
    </div>
  );
}
